import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { uuid } from '../core/util';
import { matchBosses, matchDeck, type SideLoadout } from '../meta/profile';
import type { BoardMode, Side } from '../data/types';
import type { Settings } from '../telemetry/telemetry';
import { startAutoSearch } from './auto';
import { startPhotonSearch } from './photon';
import type { FoundMatch, NetCommand, NetLink, SearchHandle } from './types';

const CHANNEL = 'peasents-mm';
const LAST_MATCH = 'peasents.lastOnlineMatch';

interface Hello {
  id: string;
  commander: string;
  levels: Record<string, number>;
  crit: number;
  deck: string[];
  bosses: string[];
}

interface MatchPayload {
  status?: string;
  match_id?: string;
  seed?: number | string;
  start_at?: number | string;
  side?: number;
  opponent_commander?: string;
  opponent_levels?: Record<string, number>;
  opponent_crit?: number;
}

export function supabaseCredentials(settings: Settings): { url: string; key: string } | null {
  const url = (settings.supabaseUrl || import.meta.env.VITE_SUPABASE_URL || '').trim();
  const key = (settings.supabaseAnonKey || import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim();
  if (!url || !key) return null;
  return { url, key };
}

export function photonAppId(settings: Settings): string {
  return (settings.photonAppId || import.meta.env.VITE_PHOTON_APP_ID || '').trim();
}

export function onlineConfigured(settings: Settings): boolean {
  return photonAppId(settings) !== '' || supabaseCredentials(settings) !== null;
}

function clientFor(settings: Settings): SupabaseClient | null {
  const creds = supabaseCredentials(settings);
  if (!creds) return null;
  return createClient(creds.url, creds.key, { auth: { persistSession: false, autoRefreshToken: false } });
}

function num(value: number | string | undefined, fallback = 0): number {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function loadoutOf(levels: Record<string, number> | undefined, crit: number): SideLoadout {
  return { levels: { ...(levels ?? {}) }, crit };
}

/**
 * Looks for another player for up to 60 seconds, then resolves null so the seat goes to a bot.
 * Public relays pair phones and computers with no account. A same-computer channel pairs
 * two windows on this machine immediately. Photon and Supabase join in only when configured.
 */
export function searchOpponent(opts: {
  settings: Settings;
  commander: string;
  loadout: SideLoadout;
  deck: string[];
  bosses: string[];
  board?: BoardMode;
  onStatus: (detail: string, secondsLeft: number, found: boolean) => void;
}): SearchHandle & { promise: Promise<FoundMatch | null> } {
  const photonId = photonAppId(opts.settings);
  const timeoutMs = 60_000;
  const session: Hello = {
    id: uuid(),
    commander: opts.commander,
    levels: opts.loadout.levels,
    crit: opts.loadout.crit,
    deck: opts.deck,
    bosses: opts.bosses,
  };

  let settled = false;
  let resolveOut: (value: FoundMatch | null) => void = () => {};
  const promise = new Promise<FoundMatch | null>((resolve) => {
    resolveOut = resolve;
  });

  const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL) : null;
  const sb = clientFor(opts.settings);
  const timers: number[] = [];
  let photonStop: (() => void) | null = null;
  let autoStop: (() => void) | null = null;
  let cancelled = false;
  const deadline = Date.now() + timeoutMs;
  const storedMatch = localStorage.getItem(LAST_MATCH);
  const ignore = storedMatch && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(storedMatch) ? storedMatch : null;

  const stopSearch = (): void => {
    for (const t of timers) window.clearInterval(t);
    timers.length = 0;
    bc?.close();
    if (sb) void sb.rpc('cancel_queue', { p_player: session.id });
    photonStop?.();
    photonStop = null;
    autoStop?.();
    autoStop = null;
  };

  const finish = (found: FoundMatch | null): void => {
    if (settled) {
      found?.link.close();
      return;
    }
    settled = true;
    stopSearch();
    if (cancelled || !found) {
      found?.link.close();
      resolveOut(null);
      return;
    }
    if (!found.matchId.startsWith('local:')) localStorage.setItem(LAST_MATCH, found.matchId);
    resolveOut(found);
  };

  const take = (found: FoundMatch): void => {
    opts.onStatus('Opponent found', Math.max(0, Math.ceil((deadline - Date.now()) / 1000)), true);
    finish(found);
  };

  bc?.addEventListener('message', (ev: MessageEvent) => {
    const msg = ev.data as {
      type?: string;
      id?: string;
      to?: string;
      from?: string;
      seed?: number;
      startAt?: number;
      board?: BoardMode;
      commander?: string;
      levels?: Record<string, number>;
      crit?: number;
      deck?: string[];
      bosses?: string[];
      matchId?: string;
    };
    if (!msg || settled || msg.id === session.id || msg.from === session.id) return;
    if (msg.type === 'hello' && msg.id) {
      if (session.id < msg.id) {
        const seed = (Date.now() % 2147483646) + 1;
        const startAt = Date.now() + 1200;
        const matchId = `local:${seed}:${startAt}`;
        bc?.postMessage({
          type: 'accept',
          to: msg.id,
          from: session.id,
          seed,
          startAt,
          matchId,
          board: opts.board ?? 'arrows',
          commander: session.commander,
          levels: session.levels,
          crit: session.crit,
          deck: session.deck,
          bosses: session.bosses,
        });
        take(localMatch(0, seed, startAt, matchId, msg.commander ?? 'vael', loadoutOf(msg.levels, msg.crit ?? 0), session, opts.board ?? 'arrows', msg.deck, msg.bosses));
      }
    } else if (msg.type === 'accept' && msg.to === session.id && msg.matchId && msg.startAt && msg.seed) {
      take(localMatch(1, msg.seed, msg.startAt, msg.matchId, msg.commander ?? 'vael', loadoutOf(msg.levels, msg.crit ?? 0), session, msg.board === 'painted' ? 'painted' : 'arrows', msg.deck, msg.bosses));
    }
  });

  timers.push(
    window.setInterval(() => {
      if (settled) return;
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      opts.onStatus(photonId ? 'Looking on Photon for another player' : 'Looking for a phone, iPad, or computer', left, false);
      bc?.postMessage({ type: 'hello', ...session });
      if (Date.now() >= deadline) finish(null);
    }, 400),
  );

  if (sb) {
    const poll = async (): Promise<void> => {
      if (settled) return;
      try {
        const waiting = await sb.rpc('poll_match', { p_player: session.id, p_ignore: ignore });
        const hit = waiting.data as MatchPayload | null;
        if (!waiting.error && hit?.status === 'matched') {
          take(remoteMatch(sb, session, hit));
          return;
        }
        const joined = await sb.rpc('request_match', {
          p_player: session.id,
          p_commander: session.commander,
          p_levels: session.levels,
          p_crit: session.crit,
        });
        const row = joined.data as MatchPayload | null;
        if (!joined.error && row?.status === 'matched') take(remoteMatch(sb, session, row));
        else if (joined.error) opts.onStatus('Saved queue unreachable. Still searching.', Math.max(0, Math.ceil((deadline - Date.now()) / 1000)), false);
      } catch {
        opts.onStatus('Saved queue unreachable. Still searching.', Math.max(0, Math.ceil((deadline - Date.now()) / 1000)), false);
      }
    };
    void poll();
    timers.push(window.setInterval(() => void poll(), 800));
  }

  if (!photonId) {
    autoStop = startAutoSearch({
      commander: opts.commander,
      loadout: opts.loadout,
      deck: opts.deck,
      bosses: opts.bosses,
      board: opts.board,
      onFound: (found) => take(found),
      onStatus: (detail) => {
        if (!settled) opts.onStatus(detail, Math.max(0, Math.ceil((deadline - Date.now()) / 1000)), false);
      },
    });
  }

  if (photonId) {
    photonStop = startPhotonSearch({
      appId: photonId,
      commander: opts.commander,
      loadout: opts.loadout,
      deck: opts.deck,
      bosses: opts.bosses,
      board: opts.board,
      onFound: (found) => take(found),
      onStatus: (detail) => {
        if (!settled) opts.onStatus(detail, Math.max(0, Math.ceil((deadline - Date.now()) / 1000)), false);
      },
    });
  }

  opts.onStatus(photonId ? 'Looking on Photon for another player' : 'Looking for a phone, iPad, or computer', Math.ceil(timeoutMs / 1000), false);

  return {
    promise,
    get cancelled() {
      return cancelled;
    },
    cancel: () => {
      cancelled = true;
      finish(null);
    },
  };
}

function localMatch(
  side: Side,
  seed: number,
  startAt: number,
  matchId: string,
  opponentCommander: string,
  opponent: SideLoadout,
  me: Hello,
  board: BoardMode = 'arrows',
  oppDeck?: string[],
  oppBosses?: string[],
): FoundMatch {
  const mine = loadoutOf(me.levels, me.crit);
  const link = broadcastLink(side, startAt, matchId, opponentCommander);
  const mineDeck = matchDeck(me.deck);
  const mineBosses = matchBosses(me.bosses);
  const theirDeck = matchDeck(oppDeck);
  const theirBosses = matchBosses(oppBosses);
  return {
    matchId,
    link,
    seed,
    board,
    loadout: side === 0 ? [mine, opponent] : [opponent, mine],
    decks: side === 0 ? [mineDeck, theirDeck] : [theirDeck, mineDeck],
    bosses: side === 0 ? [mineBosses, theirBosses] : [theirBosses, mineBosses],
  };
}

function remoteMatch(sb: SupabaseClient, me: Hello, row: MatchPayload): FoundMatch {
  const side = (row.side === 1 ? 1 : 0) as Side;
  const mine = loadoutOf(me.levels, me.crit);
  const opponent = loadoutOf(row.opponent_levels, num(row.opponent_crit));
  const matchId = String(row.match_id ?? '');
  const link = supabaseLink(sb, side, num(row.start_at, Date.now() + 2000), matchId, row.opponent_commander ?? 'vael');
  return {
    matchId,
    link,
    seed: Math.max(1, Math.floor(num(row.seed, 1))),
    loadout: side === 0 ? [mine, opponent] : [opponent, mine],
  };
}

function broadcastLink(localSide: Side, startAt: number, matchId: string, opponentCommander: string): NetLink {
  const bc = new BroadcastChannel(CHANNEL);
  const inbox: NetCommand[] = [];
  let last = Date.now();
  bc.onmessage = (ev: MessageEvent) => {
    const msg = ev.data as { type?: string; matchId?: string; cmd?: NetCommand };
    if (!msg || msg.matchId !== matchId) return;
    if (msg.type === 'ping') last = Date.now();
    if (msg.type === 'cmd' && msg.cmd && msg.cmd.side !== localSide) {
      inbox.push(msg.cmd);
      last = Date.now();
    }
  };
  const ping = window.setInterval(() => bc.postMessage({ type: 'ping', matchId }), 1000);
  return {
    localSide,
    startAt,
    opponentCommander,
    send: (cmd) => bc.postMessage({ type: 'cmd', matchId, cmd }),
    take: () => inbox.splice(0, inbox.length),
    silenceMs: () => Date.now() - last,
    close: () => {
      window.clearInterval(ping);
      bc.close();
    },
  };
}

function supabaseLink(sb: SupabaseClient, localSide: Side, startAt: number, matchId: string, opponentCommander: string): NetLink {
  const inbox: NetCommand[] = [];
  const pending: NetCommand[] = [];
  let ready = false;
  let last = Date.now();
  const channel = sb.channel(`match:${matchId}`, { config: { broadcast: { self: false } } });
  const publish = (event: string, payload: unknown): void => {
    void channel.send({ type: 'broadcast', event, payload });
  };
  channel.on('broadcast', { event: 'cmd' }, ({ payload }) => {
    const cmd = payload as NetCommand;
    if (cmd && cmd.side !== localSide) {
      inbox.push(cmd);
      last = Date.now();
    }
  });
  channel.on('broadcast', { event: 'ping' }, () => {
    last = Date.now();
  });
  channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      ready = true;
      for (const cmd of pending) publish('cmd', cmd);
      pending.length = 0;
    }
  });
  const ping = window.setInterval(() => {
    if (ready) publish('ping', { t: Date.now() });
  }, 1000);
  return {
    localSide,
    startAt,
    opponentCommander,
    send: (cmd) => {
      if (!ready) pending.push(cmd);
      else publish('cmd', cmd);
    },
    take: () => inbox.splice(0, inbox.length),
    silenceMs: () => Date.now() - last,
    close: () => {
      window.clearInterval(ping);
      void sb.removeChannel(channel);
    },
  };
}
